import React, { useEffect, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { Play, Trophy, Volume2 } from 'lucide-react';
import mapi from './api';
import { useMobile } from './MobileContext';
import { MobileShell, MobileHeader, C } from './MobileShell';
import { LEVELS } from './quizData';
import { QuizResult } from './MobileQuiz';

const NOTE_NAMES = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'H', 'C'];
const WHITE = [0, 2, 4, 5, 7, 9, 11, 12];
const BLACK = [1, 3, 6, 8, 10];
const POOLS = { easy: [0, 2, 4, 5, 7], medium: [0, 2, 4, 5, 7, 9, 11, 12], hard: Array.from({ length: 13 }, (_, i) => i) };
const LENGTHS = { easy: 3, medium: 4, hard: 6 };
const ROUNDS = 5;

let audioCtx = null;
function playTone(semi, dur = 0.4) {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const o = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    o.type = 'triangle';
    o.frequency.value = 261.63 * Math.pow(2, semi / 12);
    g.gain.setValueAtTime(0.0001, audioCtx.currentTime);
    g.gain.linearRampToValueAtTime(0.5, audioCtx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + dur);
    o.connect(g);
    g.connect(audioCtx.destination);
    o.start();
    o.stop(audioCtx.currentTime + dur + 0.05);
  } catch (_e) { /* Audio nicht verfügbar */ }
}

function randomMelody(level) {
  const pool = POOLS[level];
  return Array.from({ length: LENGTHS[level] }, () => pool[Math.floor(Math.random() * pool.length)]);
}

function Piano({ onKey, active, disabled }) {
  const keyBase = 'flex items-end justify-center pb-2 text-[10px] font-semibold select-none transition-colors';
  return (
    <div className="relative w-full" style={{ height: 170 }} data-testid="melody-piano">
      <div className="flex h-full gap-[2px]">
        {WHITE.map((semi) => (
          <button key={semi} disabled={disabled} onClick={() => onKey(semi)}
                  data-testid={`piano-key-${semi}`}
                  className={`${keyBase} flex-1 rounded-b-lg`}
                  style={{ background: active === semi ? '#fca5a5' : '#f2f2f4', color: '#333' }}>
            {NOTE_NAMES[semi]}
          </button>
        ))}
      </div>
      {BLACK.map((semi) => {
        const whiteIdx = WHITE.filter((w) => w < semi).length;
        return (
          <button key={semi} disabled={disabled} onClick={() => onKey(semi)}
                  data-testid={`piano-key-${semi}`}
                  className={`${keyBase} absolute top-0 rounded-b-md z-10`}
                  style={{ left: `${(whiteIdx / 8) * 100 - 4.5}%`, width: '9%', height: '58%',
                           background: active === semi ? '#7f1d1d' : '#111114',
                           border: `1px solid ${C.border}`, color: '#ccc' }}>
            {NOTE_NAMES[semi]}
          </button>
        );
      })}
    </div>
  );
}

export default function MobileMelody() {
  const { me, loading } = useMobile();
  const [level, setLevel] = useState(null);
  const [myScores, setMyScores] = useState({});
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (me) mapi.get('/app/quiz/my-scores').then((r) => setMyScores(r.data)).catch(() => {});
  }, [me, level]);

  if (loading) return <MobileShell><div className="flex-1 flex items-center justify-center" style={{ color: C.dim }}>Laden…</div></MobileShell>;
  if (!me) return <Navigate to="/m/login" replace />;

  if (done) {
    return <QuizResult cat="melodie" level={done.level} score={done.score} total={ROUNDS}
                       label="Melodie raten" onExit={() => { setDone(null); setLevel(null); }} />;
  }
  if (level) return <MelodyRun level={level} onDone={(score) => { setDone({ level, score }); }}
                              onExit={() => setLevel(null)} />;

  return (
    <MobileShell>
      <MobileHeader title="Melodie raten" backTo="/m/tools" />
      <div className="px-4 pt-4">
        <p className="text-xs mb-4" style={{ color: C.dim }}>
          Hör dir die Melodie an und spiele sie auf der Klaviatur nach — {ROUNDS} Runden pro Spiel.
        </p>
        <div className="space-y-2">
          {LEVELS.map((l) => {
            const best = myScores[`melodie:${l.k}`];
            return (
              <button key={l.k} onClick={() => setLevel(l.k)} data-testid={`melody-level-${l.k}`}
                      className="w-full flex items-center gap-3 px-4 py-4 rounded-xl text-left active:opacity-70"
                      style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text }}>
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: l.color }} />
                <span className="flex-1">
                  <span className="block text-sm font-semibold">{l.label}</span>
                  <span className="block text-xs" style={{ color: C.dim }}>{LENGTHS[l.k]} Töne{l.k === 'hard' ? ' · mit Halbtönen' : ''}</span>
                </span>
                {best && (
                  <span className="text-xs flex items-center gap-1" style={{ color: C.dim }}>
                    <Trophy className="w-3.5 h-3.5" style={{ color: '#eab308' }} />
                    Best: {best.score}/{best.total}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </MobileShell>
  );
}

function MelodyRun({ level, onDone, onExit }) {
  const [round, setRound] = useState(1);
  const [melody, setMelody] = useState(() => randomMelody(level));
  const [pos, setPos] = useState(0);
  const [score, setScore] = useState(0);
  const [phase, setPhase] = useState('listen'); // listen | input | won | lost
  const [activeKey, setActiveKey] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const levelInfo = LEVELS.find((l) => l.k === level);

  const playMelody = async () => {
    if (playing) return;
    setPlaying(true);
    for (const semi of melody) {
      if (!mounted.current) return;
      setActiveKey(semi);
      playTone(semi);
      await new Promise((r) => setTimeout(r, 550));
    }
    if (!mounted.current) return;
    setActiveKey(null);
    setPlaying(false);
    setPlayed(true);
    if (phase === 'listen') setPhase('input');
  };

  const onKey = (semi) => {
    playTone(semi);
    if (phase !== 'input' || playing) return;
    if (semi === melody[pos]) {
      const nextPos = pos + 1;
      if (nextPos >= melody.length) { setScore((s) => s + 1); setPhase('won'); }
      else setPos(nextPos);
    } else {
      setPhase('lost');
    }
  };

  const nextRound = () => {
    if (round >= ROUNDS) { onDone(score); return; }
    setRound(round + 1);
    setMelody(randomMelody(level));
    setPos(0);
    setPhase('listen');
    setPlayed(false);
  };

  return (
    <MobileShell>
      <MobileHeader title={`Melodie raten · ${levelInfo.label}`} backTo={undefined} />
      <div className="px-4 pt-4 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-2 text-xs" style={{ color: C.dim }}>
          <span data-testid="melody-progress-text">Runde {round} / {ROUNDS}</span>
          <span>Richtig: {score}</span>
        </div>
        <div className="h-1.5 rounded-full mb-5" style={{ background: C.card }}>
          <div className="h-1.5 rounded-full transition-all duration-300"
               style={{ background: levelInfo.color, width: `${((round - 1) / ROUNDS) * 100}%` }} />
        </div>

        <div className="flex items-center gap-1.5 justify-center mb-4" data-testid="melody-dots">
          {melody.map((semi, i) => {
            let bg = C.card, label = '?';
            if (phase === 'won' || phase === 'lost') { bg = i < pos || phase === 'won' ? '#14331f' : '#3a1d20'; label = NOTE_NAMES[semi]; }
            else if (i < pos) { bg = '#14331f'; label = NOTE_NAMES[semi]; }
            return (
              <span key={i} className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold"
                    style={{ background: bg, border: `1px solid ${C.border}`, color: C.text }}>
                {label}
              </span>
            );
          })}
        </div>

        {phase === 'won' && (
          <p className="text-center text-sm font-semibold mb-3" style={{ color: '#22c55e' }} data-testid="melody-round-won">
            Richtig nachgespielt! 🎉
          </p>
        )}
        {phase === 'lost' && (
          <p className="text-center text-sm font-semibold mb-3" style={{ color: C.red }} data-testid="melody-round-lost">
            Leider falsch — das war die Melodie.
          </p>
        )}

        {(phase === 'listen' || phase === 'input') ? (
          <button onClick={playMelody} disabled={playing} data-testid="melody-play-btn"
                  className="mx-auto mb-4 flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white active:opacity-80"
                  style={{ background: playing ? C.cardHover : C.red }}>
            {playing ? <Volume2 className="w-4 h-4 animate-pulse" /> : <Play className="w-4 h-4" />}
            {playing ? 'Spielt…' : played ? 'Nochmal anhören' : 'Melodie abspielen'}
          </button>
        ) : (
          <button onClick={nextRound} data-testid="melody-next-btn"
                  className="mx-auto mb-4 px-6 py-3 rounded-full text-sm font-bold text-white active:opacity-80"
                  style={{ background: C.red }}>
            {round >= ROUNDS ? 'Ergebnis anzeigen' : 'Nächste Runde'}
          </button>
        )}

        <div className="mt-auto pb-3">
          <Piano onKey={onKey} active={activeKey} disabled={playing} />
          <button onClick={onExit} className="mt-4 mb-2 text-xs underline block mx-auto" style={{ color: C.dim }}
                  data-testid="melody-abort-btn">Spiel abbrechen</button>
        </div>
      </div>
    </MobileShell>
  );
}
